import { AutocompleteRoot } from "./Autocomplete.styles";
import AutocompleteInput from "./AutocompleteInput/AutocompleteInput";
import clsx from "clsx";
import useAutocomplete from "./hooks/useAutocomplete";
import { AutocompleteProps } from "./Autocomplete.types";

/**
 * `Autocomplete` is a searchable select component that wraps the base `Select`
 * with a text input so users can filter options by typing.
 *
 * ## Behaviour
 * - The dropdown opens automatically as the user types.
 * - Options are filtered client-side using a case-insensitive label-contains
 *   check (customisable via `filterOption` on the underlying hook).
 * - While `isLoading` is `true` the option list is replaced by a single
 *   disabled entry showing `loadingText` (or the i18n `"loading"` fallback).
 * - When no options match the current search, a disabled entry shows
 *   `nothingToShow` (or the i18n `"noResultsForThisSearch"` fallback).
 * - The selected value and the search text can each be controlled independently.
 * - Integrates with HTML forms: registers its value through `useFormNotification`
 *   and respects the `required` and `name` props.
 *
 * @template T - The type of each option's `value` field.
 *
 * @example
 * ```tsx
 * <Autocomplete
 *   options={[{ label: "Option A", value: "a" }, { label: "Option B", value: "b" }]}
 *   placeholder="Search…"
 *   onChange={(val) => console.log(val)}
 * />
 * ```
 */
function Autocomplete<T>({
    className,
    size = "lg",
    defaultValue,
    value,
    onChange,
    options,
    defaultSearch = "",
    search: searchProp,
    onSearchChange,
    isLoading,
    name,
    required,
    readonly,
    nothingToShow,
    loadingText,
    placeholder,
    ...rest
}: AutocompleteProps<T>) {
    const { selectProps, inputProps } = useAutocomplete<T>({
        defaultSearch,
        search: searchProp,
        onSearchChange,
        defaultValue,
        value,
        onChange,
        options,
        isLoading,
        name,
        required,
        loadingText,
        nothingToShow,
    });

    // skipcq: JS-0424
    return (
        <AutocompleteRoot
            {...selectProps}
            required={required}
            readonly={readonly}
            dropdownElement={<></>} // skipcq: JS-0424
            className={clsx("autocomplete", className)}
            renderValue={() => <AutocompleteInput placeholder={placeholder} readonly={readonly} {...inputProps} size={size} />}
            size={size}
            {...rest}
        />
    );
}

export default Autocomplete;
